import { forwardRef, ForwardedRef, ReactElement, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { buildAvatarImageSource } from '../../utils/buildAvatarImageSource';
import { cropPublicId } from '../../utils/cropPublicId';
import { Skeleton } from '../feedback/Skeleton';
import { IconKebab } from '../icon/IconKebab';
import { IconLink } from '../icon/IconLink';
import { IconProduct } from '../icon/IconProduct';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex } from '../layout/Flex';
import { Avatar } from '../media/Avatar';
import { MediaCover } from '../media/MediaCover';
import { Typography } from '../typography/Typography';
import { MediaPreview } from './MediaPreview';
import { PostAdCardCompact } from './PostAdCompactCard';
import { PostCardStatus } from './PostCardStatus/PostCardStatus';
import { getContentIcon } from './getContentIcon';
import { type AdCardUI, type PostUI } from './types';

export const POST_CARD_COMPACT_CONTENT_HEIGHT = 80;

const DEFAULT_ICON_PROPS = {
    color: 'var(--control-secondary-blur-color)',
    size: 20,
};

const IconWrapper = styled(Box)
    .withConfig({ displayName: 'IconWrapper' })
    .attrs((props) => ({
        position: 'absolute',
        zIndex: 1,
        p: 0.25,
        borderRadius: 1,
        background: 'var(--control-secondary-blur-background)',
        ...props,
    }))``;

const ProfileInfo = ({ post, handleClick }: { post: PostUI; handleClick: VoidFunction }) => (
    <Box>
        {post.business ? (
            <Flex direction="row" alignItems="center" width="100%">
                <Flex
                    as={Link}
                    to={post.business.url}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    textDecoration="none"
                >
                    <Avatar
                        width={24}
                        title={post.business.displayName}
                        src={post.business.avatarUrl}
                        border="1px solid var(--divider)"
                    />
                    <Typography variant="caption" weight="bold">
                        {cropPublicId(post.business.publicId)}
                    </Typography>
                </Flex>
            </Flex>
        ) : null}
        {post.ownersData ? (
            <Flex
                direction="row"
                alignItems="center"
                spacing={1}
                textDecoration="none"
                width="100%"
                onClick={handleClick}
                cursor="pointer"
            >
                <Flex position="relative">
                    <Avatar
                        width={24}
                        title={post.ownersData.author.displayName}
                        src={buildAvatarImageSource(post.ownersData.author.avatarUrl)}
                    />
                    <Box
                        position="absolute"
                        right="-3px"
                        top="-3px"
                        borderRadius="50%"
                        backgroundColor="#FFF"
                        border="solid 2px transparent"
                    >
                        <IconLink size={10} />
                    </Box>
                </Flex>
                <Flex>
                    <Typography variant="caption" weight="bold">
                        {post.ownersData.shop.displayUrl}
                    </Typography>
                </Flex>
            </Flex>
        ) : null}
    </Box>
);

export const PostCardCompactText = styled(Typography)
    .withConfig({ displayName: 'PostCardCompactText' })
    .attrs((props) => ({
        ...props,
        variant: 'caption',
        cursor: 'pointer',
    }))`
    height: 28px;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    word-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
`;

export interface PostCompactCardProps {
    post: (PostUI & { isAd?: false }) | AdCardUI;
    index: number;
    renderMenu?: (trigger: ReactElement) => ReactElement | null;
    onPostCardClick?: (
        id: PostUI['id'],
        index: number,
        postUrl: PostUI['url'],
        postBusinessId: string | undefined,
        logContext: PostUI['logContext'],
        djRequestId: PostUI['djRequestId'],
    ) => void;
    fromBusinessId?: string;
    fromCollectionId?: string;
    showContent?: boolean;
    adInterval?: number;
}

export const PostCardCompact = forwardRef(
    (props: PostCompactCardProps, ref: ForwardedRef<HTMLDivElement>) => {
        const { post, showContent, renderMenu, index, adInterval = 0, onPostCardClick } = props;
        if (post.isAd) {
            return <PostAdCardCompact postIndex={index} adInterval={adInterval} />;
        }
        const ContentIcon = getContentIcon(post.content);

        const handleClick = useCallback(() => {
            onPostCardClick?.(
                post.id,
                index,
                post.url,
                post.business?.id,
                post.logContext,
                post.djRequestId,
            );
        }, [
            index,
            onPostCardClick,
            post.business?.id,
            post.djRequestId,
            post.id,
            post.logContext,
            post.url,
        ]);

        const getCardContent = useCallback(
            () => (
                <Flex spacing={0.5} px={0.5} pt={1} pb={2}>
                    <ProfileInfo handleClick={handleClick} post={post} />
                    <PostCardCompactText onClick={handleClick}>{post.text}</PostCardCompactText>
                </Flex>
            ),
            [post, handleClick],
        );

        return (
            <Box
                ref={ref}
                position="relative"
                onClick={handleClick}
                cursor="pointer"
                dataTestId={DataTestId.POST_CARD_COMPACT}
            >
                <MediaCover width="100%" height="100%" ratio={1}>
                    <MediaPreview media={post.content[0]} namespace="grid" />
                </MediaCover>
                {ContentIcon ? (
                    <IconWrapper
                        right={1}
                        bottom={1 + (showContent ? POST_CARD_COMPACT_CONTENT_HEIGHT / 8 : 0)}
                        onClick={handleClick}
                        cursor="pointer"
                    >
                        <ContentIcon {...DEFAULT_ICON_PROPS} />
                    </IconWrapper>
                ) : null}
                <PostCardStatus post={post} top={1} left={1} />
                {renderMenu
                    ? renderMenu(
                          <Box position="absolute" right={1} top={1}>
                              <IconButton
                                  backgroundColor="var(--control-secondary-blur-background)"
                                  color="var(--control-secondary-blur-color)"
                                  //   variant="filled"
                                  shape="rect"
                                  size="xs"
                              >
                                  <IconKebab
                                      fontSize={16}
                                      dataTestId={DataTestId.POST_CARD_KEBAB}
                                  />
                              </IconButton>
                          </Box>,
                      )
                    : null}
                {post.products.items.length > 0 && (
                    <IconWrapper
                        position="absolute"
                        p={0.5}
                        right={renderMenu ? 5 : 1}
                        top={1}
                        cursor="pointer"
                        onClick={handleClick}
                    >
                        <IconProduct {...DEFAULT_ICON_PROPS} fontSize={16} size={16} />
                    </IconWrapper>
                )}
                {showContent ? getCardContent() : null}
            </Box>
        );
    },
);

export const PostCardCompactSkeleton = styled(Skeleton)
    .withConfig({ displayName: 'PostCardCompactSkeleton' })
    .attrs({
        variant: 'rect',
        width: '100%',
        height: '100%',
    })``;
